import * as THREE from 'three';
import { useEffect } from 'react';

import { useModelViewer } from '../../../../hooks/useModelViewer';

import { makeUnclickableThreeObject } from '../../../../utils/makeUnclickableThreeObject';

const createSphereHelper = (size: number, originPos: THREE.Vector3) => {
  const geo = new THREE.SphereGeometry(size / 100, 16, 16);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xaaaaaa,
    depthTest: false,
    depthWrite: false
  });
  const sphere = new THREE.Mesh(geo, mat);
  originPos.copy(sphere.position);
  makeUnclickableThreeObject(sphere);

  return sphere;
};

function useModelViewerGridAxesHelper() {
  const { modelViewerEl, scene } = useModelViewer();

  useEffect(() => {
    let state = {} as Record<any, any>;

    const createHelpers = () => {
      if (!modelViewerEl || !scene) return;

      const createHelpers = () => {
        const size = modelViewerEl.getDimensions();
        const avgSize = (size.x + size.y + size.z) / 3;

        const grid = new THREE.GridHelper(avgSize * 2, 20);
        grid.material.opacity = 0.35;
        grid.material.transparent = true;
        makeUnclickableThreeObject(grid);

        const axes = new THREE.AxesHelper(avgSize || 1);
        (Array.isArray(axes.material)
          ? axes.material
          : [axes.material]
        ).forEach((mat) => {
          mat.depthTest = false;
          mat.depthWrite = false;
        });

        axes.renderOrder = 9997;

        makeUnclickableThreeObject(axes);

        const originPos = new THREE.Vector3();
        scene.model.getWorldPosition(originPos);

        const originDotOutline = createSphereHelper(
          (avgSize / 100) * 1.2,
          originPos
        );
        originDotOutline.renderOrder = 9998;

        const originDot = createSphereHelper(avgSize / 100, originPos);
        originDot.renderOrder = 9999;

        state = {
          grid,
          axes,
          originDotOutline,
          originDot
        };
      };

      const addHelpers = () => {
        createHelpers();

        scene.model.add(state.grid);
        scene.model.add(state.axes);
        scene.model.add(state.originDotOutline);
        scene.model.add(state.originDot);
        scene.queueRender();
      };

      const removeHelpers = () => {
        state.originDotOutline.removeFromParent();
        state.originDot.removeFromParent();
        state.grid.removeFromParent();
        state.axes.removeFromParent();
        scene.queueRender();
      };

      const updateHelpers = () => {
        removeHelpers();
        addHelpers();
      };

      return {
        addHelpers,
        removeHelpers,
        updateHelpers
      };
    };

    const { addHelpers, removeHelpers, updateHelpers } = createHelpers();

    modelViewerEl?.loaded && addHelpers();

    modelViewerEl?.addEventListener('load', updateHelpers);

    return () => {
      modelViewerEl?.removeEventListener('load', updateHelpers);
      removeHelpers();
    };
  }, [modelViewerEl, scene]);
}

export default useModelViewerGridAxesHelper;
